import NiceModal from "@ebay/nice-modal-react";

import SimpleModal from "components/modals/SimpleModal";
import { Deactivation, Question, Report, TErrCodes, User } from "types/types";
import {
  useFetchData,
  useFetchInfiniteData,
  useFetchPaginatedData,
  useGenericMutation,
} from "../useAxios";

export const useGetUsers = ({
  page,
  size,
  grade,
  searchText,
  searchType,
  sort = "createdDesc",
  startDate,
  endDate,
}: {
  page: number;
  size: number;
  grade?: string;
  searchText?: string;
  searchType?: string;
  sort?: string;
  startDate?: string;
  endDate?: string;
}) =>
  useFetchPaginatedData<User>({
    endpoint: "v1/users",
    params: {
      page,
      size,
      grade,
      searchText,
      searchType,
      sort,
      startDate,
      endDate,
    },
    queryKey: [
      "users",
      grade ?? "all",
      searchText ?? "",
      searchType ?? "",
      page,
      size,
      sort,
      startDate ?? "",
      endDate ?? "",
    ],
    keepPreviousData: true,
  });

export const useGetMonitoringUsers = ({
  page,
  size,
  connectionStatus,
  grade,
  linkingStatus,
  sort,
  searchText,
}: {
  page: number;
  size: number;
  connectionStatus: number | "all";
  grade: number | "all";
  linkingStatus: string;
  sort?: string;
  searchText: string;
}) =>
  useFetchPaginatedData<User>({
    endpoint: "v1/users/monitoring",
    params: {
      page,
      size,
      connectionStatus,
      grade,
      linkingStatus,
      sort,
      searchText,
    },
    queryKey: [
      "users_monitoring",
      page,
      size,
      connectionStatus,
      grade,
      linkingStatus,
      sort ?? "dateDesc",
      searchText,
    ],
    keepPreviousData: true,
  });

export const useGetUser = ({ id }: { id?: number }) =>
  useFetchData<User>({
    endpoint: `v1/users/${id}`,
    queryKey: ["user", id ?? ""],
    enabled: !!id,
  });

export const useNewUser = () =>
  useGenericMutation<{
    name: string;
    userId: string;
    password: string;
    phone: string;
    residentId1: string;
    residentId2: string;
    daesungId: string;
    daesungPassword: string;
    bank: string;
    bankNumber: string;
  }>({
    endpoint: "v1/users/",
    invalidateQueries: [["users"]],
    meta: {
      errCode: TErrCodes.NONE,
    },
    showLoading: false,
    onError: (e: any) => {
      if (e.response?.data?.errMsg) {
        NiceModal.show(SimpleModal, {
          showCancel: false,
          message: e.response.data.errMsg,
        });
      }
    },
  });

export const useUpdateUser = () =>
  useGenericMutation<{
    id: number;
    password?: string;
    phone: string;
    residentId1: string;
    residentId2: string;
    daesungPassword?: string;
    bank: string;
    bankNumber: string;
  }>({
    method: "put",
    endpoint: ({ id }) => `v1/users/${id}`,
    invalidateQueries: (_, { id }) => [["user", id], ["users"]],
    // meta: {
    //   errCode: TErrCodes.NONE,
    // },
    // showLoading: false,
  });

export const useDeleteUsers = () =>
  useGenericMutation<{ ids: number[] }>({
    endpoint: () => `v1/users/delete`,
    invalidateQueries: [["users"]],
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "계정 삭제",
        message: "삭제 완료되었습니다.",
        showCancel: false,
      });
    },
  });

export const useGetMonitoringUsersCount = () =>
  useFetchData<{
    totalUsers: number;
    connectedUsers: number;
    unlinkedUsers: number;
    date: Date;
  }>({
    endpoint: "v1/users/monitoring/count",
    queryKey: ["users_monitoring_count"],
    meta: { errCode: TErrCodes.NONE },
  });

export const useGetReports = ({ id }: { id: number }) =>
  useFetchData<Report[]>({
    endpoint: `v1/users/${id}/reports`,
    queryKey: ["user_reports", id ?? ""],
  });

export const useGetDeactivations = ({ id }: { id: number }) =>
  useFetchData<Deactivation[]>({
    endpoint: `v1/users/${id}/deactivations`,
    queryKey: ["user_deactivations", id ?? ""],
  });

export const useClearReports = () =>
  useGenericMutation<{ id: number }>({
    endpoint: ({ id }) => `v1/users/${id}/reports/clear`,
    invalidateQueries: (_, { id }) => [
      ["users_monitoring"],
      ["user_reports", id ?? ""],
    ],
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "계정 활성화",
        message:
          "계정이 재활성화되었으며, 경고 횟수는 0으로 초기화되었지만 신고 기록은 남아 있습니다.",
        showCancel: false,
      });
    },
  });

export const useGetIncomes = ({
  id,
  size,
  startDate,
  endDate,
  answerType,
}: {
  id: number;
  size: number;
  startDate?: string;
  endDate?: string;
  answerType?: number;
}) =>
  useFetchInfiniteData<Question>({
    endpoint: "v1/questions",
    params: {
      userId: id,
      size,
      startDate,
      endDate,
      answerType,
    },
    queryKey: [
      "incomes",
      id,
      startDate ?? "",
      endDate ?? "",
      answerType ?? "",
      size,
    ],
    keepPreviousData: true,
    showLoading: true,
  });

export const useGetIncomesDetail = ({
  id,
  startDate,
  endDate,
}: {
  id: number;
  startDate?: string;
  endDate?: string;
}) =>
  useFetchData<{
    bonusCount: number;
    completedCount: number;
    reanswerCount: number;
    warningCount: number;
    totalReward: number;
    startDate: string;
    endDate: string;
    gradeId: number;
  }>({
    endpoint: `v1/users/${id}/incomes/detail`,
    params: {
      startDate,
      endDate,
    },
    queryKey: ["incomes_detail", id, startDate ?? "", endDate ?? ""],
    keepPreviousData: true,
    showLoading: true,
  });

export const useCheckPassword = () =>
  useGenericMutation<{ id: number; password: string }, { isCorrect: boolean }>({
    endpoint: ({ id }) => `v1/users/${id}/password/check`,
    showLoading: false,
    meta: {
      errCode: TErrCodes.NONE,
    },
  });
